import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, FormControl, FormGroup } from "@angular/forms";
import { SubSink } from "subsink";
import { BMI_DATE, BMI_PERCENTILE, BMI_VALUE } from "../../../platform/modules/member/chase-detail/chase-detail-chart/attributes";
import { ChaseDetailService } from "../../../platform/modules/member/chase-detail/chase-detail.service";
import { ArrayHelper } from "../../../utilities/contracts/array-helper";
import { DateHelper } from "../../../utilities/contracts/date-helper";
import { StringHelper } from "../../../utilities/contracts/string-helper";
import { DynamicControlDirective } from "../../dynamic-control-component.model";
import { DynamicFormEvent } from "../../dynamic-form-event.model";
import { FormService } from "../../form.service";
import { HighlightService } from "../../highlighter/highlight.service";
import { BmiGroup } from "./bmi-group.model";

@Component({
  selector: "form-bmi-group",
  templateUrl: "./bmi-group.component.html",
  styleUrls: [
    "../dynamic-group.scss",
    "../../highlighter/highlight.scss",
    "./bmi-group.component.scss",
  ],
  providers: [
    HighlightService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BmiGroupComponent extends DynamicControlDirective<BmiGroup> implements OnInit, AfterViewChecked, OnDestroy {
  private sink = new SubSink();
  private previousAge = 0;

  constructor(
    readonly highlighter: HighlightService,
    private readonly chaseDetailService: ChaseDetailService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly formService: FormService
  ) {
    super();
  }

  ngOnInit() {
    this.sink.add(
      this.chaseDetailService.chaseDetailsChange.subscribe(chaseDetails => {
        this.model.memberDateOfBirth = chaseDetails.find(item => item.key === "DOB").value;
      }),
      this.control.statusChanges
        .subscribe(() => this.changeDetector.markForCheck()),
      this.formService.updateDom
        .subscribe(() => this.changeDetector.markForCheck()),
      this.highlighter.onBlurGroup
        .subscribe(() => this.save())
    );
    this.updateInputs();
  }

  ngAfterViewChecked() {
    this.updateInputs();
  }

  ngOnDestroy() {
    this.sink.unsubscribe();
  }

  get classes(): any {
    return {
      ...this.highlighter.className(),
      "control-container__invlaid": this.hasError,
    };
  }

  save(): void {
    if (this.control.pending) {
      setTimeout(() => this.save(), 100);
    } else if (this.control.valid && this.hasAtLeastOneValue(this.control)) {
      const event = new DynamicFormEvent({
        key: this.model.key,
        type: "save",
        value: this.control.value,
        control: this.control,
        model: this.model,
      });
      this.onChange.emit(event);
    }
  }

  remove(): void {
    if (this.formService.isDisabled(this.model)) {
      return;
    }

    const controls = (this.control as FormGroup).controls as { [key: string]: FormControl };

    const event = new DynamicFormEvent({
      key: this.model.key,
      type: "delete",
      value: this.control.value,
      control: this.control,
      model: this.model,
      successFn: () => {
        for (const key in controls) {
          if (controls.hasOwnProperty(key)) {
            const control = controls[key];
            if (!ArrayHelper.isAvailable((control as any).controls)) {
              control.setValue(null);
            }
          }
        }

        this.model.controls.forEach(control => {
          (control as any).saveInfo.entityId = null;
        });
      },
    });
    this.onChange.emit(event);
  }

  handleChangeEvents(event: DynamicFormEvent): void {
    this.updateInputs();
    switch (event.type) {
      case "save":
        this.save();
        break;
      default:
        break;
    }
  }

  trackByIndex(index, item) {
    return index;
  }

  private hasAtLeastOneValue(control: AbstractControl): boolean {
    const keys = Object.keys(control.value);
    for (const key of keys) {
      const value = control.value[key];
      if (typeof value === "object" && value != null) {
        return this.hasAtLeastOneValue(value);
      } else if (value != null) {
        return true;
      }
    }

    return false;
  }

  private isCompleteRecursive(group: object): boolean {
    return Object.values(group).every(value => {
      const isAnObject = typeof value === "object" && value != null;
      return isAnObject ? this.isCompleteRecursive(value) : StringHelper.isAvailable((value || "").toString());
    });
  }

  private updateInputs() {
    const bmiDateInput = this.model.controls.find(
      (a: any) => a.saveInfo.id === BMI_DATE.attributeId
    );

    if (bmiDateInput) {
      const bmiDateValue = this.control.value[bmiDateInput.key];

      if (DateHelper.isAvailable(bmiDateValue) && DateHelper.isAvailable(this.model.memberDateOfBirth)) {
        const valueInput = this.model.controls.find(
          (a: any) => a.saveInfo.id === BMI_VALUE.attributeId
        );
        const percentInput = this.model.controls.find(
          (a: any) => a.saveInfo.id === BMI_PERCENTILE.attributeId
        );
        const ageAtEnteredDate = DateHelper.ageInYears(
          this.model.memberDateOfBirth,
          bmiDateValue
        );
        if (this.previousAge !== ageAtEnteredDate) {
          this.previousAge = ageAtEnteredDate;
          if (ageAtEnteredDate > 19) {
            valueInput.hidden = false;
            percentInput.hidden = true;
          } else {
            valueInput.hidden = true;
            percentInput.hidden = false;
          }
        }
      }
    }
  }
}
